import * as React from "react";
import { LinksProps } from "../typings";
import { observer } from "mobx-react";
import { withStores } from "src/stores";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { DefaultFlexModuleResult, LinkView } from "typings/microserviceModels/links-flex-module";
import { UitkHeading } from "uitk-react-text";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { UitkFigure, UitkImage } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkCard, UitkCardLink } from "uitk-react-cards";
import { getImageAspectRatio } from "./helpers/image-aspect-ratio";

const MODULE_NAME = "links";
const LINK_NAME = "linkscardlink";
const SECTION_NAME = "content";

const CardLink: React.FC<Omit<LinkView, "auxiliaryText" | "icon" | "latitude" | "longitude"> & { name: string }> = ({
  imageUrl,
  name,
  rfrr,
  text,
  imageAspectRatio,
  url,
}) => (
  <UitkCard>
    <UitkCardLink>
      <TrackedLink moduleName={name} href={url} rfrr={rfrr} linkName={text}>
        {text}
      </TrackedLink>
    </UitkCardLink>
    <UitkFigure ratio={getImageAspectRatio(imageAspectRatio)}>
      <UitkImage src={imageUrl} alt={text} placeholderImage lazyLoading="lazy" />
      <UitkScrim type={UitkScrimType.BOTTOM}>
        <UitkHeading tag="h3" size={5}>
          {text}
        </UitkHeading>
      </UitkScrim>
    </UitkFigure>
  </UitkCard>
);

/**
 * Carousel of linked images
 */
export const CardsCarouselLinksView = withStores(
  "flexModuleModelStore",
  "context",
  "flexViewModel"
)(
  observer((props: LinksProps) => {
    const { templateComponent, flexModuleModelStore, flexViewModel } = props;
    const {
      metadata: { id },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as DefaultFlexModuleResult | null;

    if (!model?.links?.length) {
      return null;
    }

    const pageName = flexViewModel.trackingContext.pageName;
    const { links, name, title, imageAspectRatio, lgCarouselItems, mdCarouselItems, smCarouselItems } = model;
    const { formatText } = props.l10n;
    const keyHelper = new ItemKeyHelper("links");

    const visibleItems = {
      lg: lgCarouselItems || 4,
      md: mdCarouselItems || 4,
      sm: smCarouselItems || 2,
    };

    return (
      <div data-testid="cards-carousel-links-view" id={id}>
        {title && (
          <UitkSpacing margin={{ blockend: "six" }}>
            <UitkHeading tag="h2" size={4}>
              {title}
            </UitkHeading>
          </UitkSpacing>
        )}

        <IsomorphicCarousel
          itemsVisible={visibleItems}
          buttonText={{
            nextButton: formatText("carousel.item.next"),
            prevButton: formatText("carousel.item.prev"),
          }}
          pageBy={1}
          peekWidth={12}
        >
          {links.map((link: LinkView, index: number) => (
            <div key={keyHelper.next()}>
              <CardLink
                name={name}
                imageUrl={link.imageUrl}
                rfrr={`${pageName}.${MODULE_NAME}.${LINK_NAME}${index + 1}.${SECTION_NAME}`}
                imageAspectRatio={imageAspectRatio}
                text={link.text}
                url={link.url}
              />
            </div>
          ))}
        </IsomorphicCarousel>
      </div>
    );
  })
);

export default CardsCarouselLinksView;
